/**
 * SMS 리스트. GET /api/sms. v1은 raw 메시지만 (분류·요약 X).
 */
import { useQuery } from '@tanstack/react-query';
import { type SmsListItem } from '../lib/api';
import { listSmsFromRoom } from '../lib/roomData';
import { EmptyState, PageHeader } from '../components/AppShell';
import { phoneOrFallback, shortWhen } from '../lib/format';

export function Sms() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['sms'],
    queryFn: () => listSmsFromRoom(),
    retry: false,
  });

  return (
    <>
      <PageHeader title="SMS" subtitle="수신된 문자 메시지가 자동으로 모입니다." />
      <main className="px-xl">
        {isLoading && (
          <p className="text-[13px] text-[var(--text-muted)] py-md">로딩 중…</p>
        )}
        {isError && (
          <p className="text-[13px] text-[var(--error)] py-md">
            불러오기 실패: {error instanceof Error ? error.message : String(error)}
          </p>
        )}
        {data && data.items.length === 0 && (
          <EmptyState>SMS가 쌓이면 여기에 자동으로 정리됩니다.</EmptyState>
        )}
        {data && data.items.length > 0 && (
          <ul className="space-y-sm">
            {data.items.map((item) => (
              <SmsRow key={item.id} item={item} />
            ))}
          </ul>
        )}
      </main>
    </>
  );
}

function SmsRow({ item }: { item: SmsListItem }) {
  return (
    <li className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md">
      <div className="px-lg py-md flex gap-md">
        <div className="w-8 h-8 rounded-full bg-[var(--surface-elev)] border border-[var(--border)] grid place-items-center text-[14px] text-[var(--text-secondary)] shrink-0">
          ✉
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-sm">
            <span className="text-[13px] font-medium text-[var(--text-primary)] truncate">
              {phoneOrFallback(null, item.from_phone)}
            </span>
            <span className="text-[11px] font-mono text-[var(--text-muted)] shrink-0">
              {shortWhen(item.received_at)}
            </span>
          </div>
          <p className="text-[13px] text-[var(--text-secondary)] mt-xs leading-relaxed whitespace-pre-wrap break-words">
            {item.body}
          </p>
        </div>
      </div>
    </li>
  );
}
